import { Project } from "../types";

export const projects: Project[] = [
  {
    id: "distributed-neural-network",
    title: "Distributed Neural Network Training Framework",
    description:
      "Custom implementation of distributed Stochastic Gradient Descent (SGD) for training neural networks across multiple nodes in a LAN. Built from scratch in C using raw sockets, multithreading, and custom network protocols.",
    longDescription:
      "Developed a complete distributed machine learning framework from the ground up in C for training neural networks across multiple machines. Implemented synchronous and asynchronous SGD algorithms with custom networking protocols using raw sockets. The system features workload distribution, gradient synchronization with compression, fault tolerance mechanisms, and dynamic node management. Achieved near-linear scalability up to 8 nodes.",
    technologies: [
      "C",
      "Raw Sockets",
      "POSIX Threads",
      "Distributed SGD",
      "Network Programming",
      "Distributed Systems",
      "MPI",
      "Linux",
    ],
    githubUrl: "https://github.com/autives/na",
    featured: true,
    category: "systems",
    startDate: "2023-08",
    status: "completed",
    impact: "Demonstrated systems-level understanding of distributed ML",
    metrics: [
      "7.2x speedup on 8 nodes",
      "85% communication efficiency",
      "Fault-tolerant with automatic node recovery",
    ],
  },
  {
    id: "earthquake-warning",
    title: "Earthquake Early Warning System",
    description:
      "Deep learning model for real-time earthquake prediction developed in collaboration with Duke University. Implemented advanced ML algorithms for seismic data analysis with focus on reducing false positives.",
    longDescription:
      "Advanced machine learning system using convolutional and recurrent neural networks to analyze seismic data and predict earthquakes with high accuracy. Implemented sophisticated data preprocessing pipelines, feature engineering techniques, and real-time prediction algorithms. The system processes large-scale seismic datasets from multiple stations and provides early warnings with minimal false positives while maintaining sub-100ms inference latency.",
    technologies: [
      "Python",
      "TensorFlow",
      "PyTorch",
      "Deep Learning",
      "Signal Processing",
      "NumPy",
      "Pandas",
      "Scikit-learn",
    ],
    featured: true,
    category: "ml",
    startDate: "2024-10",
    endDate: "2025-05",
    status: "completed",
    paperUrl: "In Preparation",
    impact: "Collaboration with Duke University Civil Engineering",
    metrics: [
      "25% improvement in prediction accuracy",
      "40% reduction in false positives",
      "Real-time inference <100ms",
    ],
  },
  {
    id: "v2x-communication",
    title: "5G V2X Communication Protocol Implementation",
    description:
      "Implementation and performance analysis of MQTT and ZeroMQ protocols for Vehicle-to-Everything communication over 5G networks using OMNET++. Created realistic simulation environments for protocol testing.",
    longDescription:
      "Complete simulation framework for vehicular communication protocols over 5G networks. Implemented IoT communication protocols (MQTT and ZeroMQ) from scratch in OMNET++ environment following 3GPP specifications. Created various network topologies and mobility scenarios for testing protocol performance under different conditions including urban congestion, highway mobility, and mixed environments. Developed automated analysis tools for performance metrics.",
    technologies: [
      "C++",
      "OMNET++",
      "MQTT",
      "ZeroMQ",
      "5G NR",
      "IoT",
      "Network Simulation",
      "Protocol Design",
    ],
    githubUrl: "https://github.com/timalsinaditya/MQTT-and-ZmQ-from-scratch.git",
    featured: true,
    category: "networks",
    startDate: "2024-04",
    endDate: "2025-04",
    status: "completed",
    paperUrl: "Under Review - IEEE VTC 2025",
    impact: "International collaboration with UPV, Spain",
    metrics: [
      "30% improved delivery rate",
      "Sub-20ms latency achieved",
      "95% reliability in high mobility",
    ],
  },
  {
    id: "blockchain-golang",
    title: "Blockchain Implementation with Consensus Mechanisms",
    description:
      "Custom blockchain implementation in Golang featuring proof-of-work consensus, Byzantine fault tolerance, transaction validation, and peer-to-peer networking. Includes wallet functionality and mining capabilities.",
    longDescription:
      "Developed a complete blockchain system from scratch in Golang featuring multiple consensus mechanisms (PoW, PoS, PBFT), transaction validation with digital signatures, peer-to-peer networking with gossip protocol, and Merkle tree implementation. Includes wallet functionality, mining capabilities, smart contract VM, and a cryptocurrency implementation with transaction history and balance tracking. Designed for understanding distributed consensus and Byzantine fault tolerance.",
    technologies: [
      "Golang",
      "Blockchain",
      "Cryptography",
      "P2P Networking",
      "Consensus Algorithms",
      "Distributed Systems",
      "Byzantine Fault Tolerance",
    ],
    githubUrl: "https://github.com/timalsinaditya/simple-container.git",
    featured: true,
    category: "systems",
    startDate: "2024-01",
    status: "ongoing",
    impact: "Deep dive into distributed consensus and fault tolerance",
    metrics: [
      "1000+ TPS throughput",
      "Byzantine fault tolerance",
      "Sub-second block finality",
    ],
  },
  {
    id: "audio-fft",
    title: "Real-Time Audio Signal Processing with FFT",
    description:
      "Real-time audio filtering application implementing Fast Fourier Transform with various frequency filters. Features live audio visualization and real-time signal processing with minimal latency.",
    longDescription:
      "Developed a real-time audio processing application using FFT algorithms for frequency domain analysis and filtering. Implemented various filters including low-pass, high-pass, band-pass, and notch filters with adjustable parameters. Features real-time audio visualization with spectrum analysis, waterfall display, and adjustable filter parameters. Optimized for low-latency processing (<10ms) suitable for live audio applications.",
    technologies: [
      "C",
      "Raylib",
      "FFT",
      "DSP",
      "Audio Processing",
      "Real-time Systems",
      "Signal Processing",
      "SIMD Optimization",
    ],
    githubUrl: "https://github.com/timalsinaditya/fft-of-real-time-audio.git",
    featured: false,
    category: "systems",
    startDate: "2024-09",
    status: "completed",
    impact: "Hands-on signal processing and real-time systems",
    metrics: [
      "<10ms processing latency",
      "4096-point FFT in real-time",
      "60 FPS visualization",
    ],
  },
  {
    id: "snake-rl",
    title: "Reinforcement Learning for Game AI",
    description:
      "Autonomous snake game using Q-learning and Deep Q-Networks (DQN) implemented in Rust. Features custom RL algorithms with reward shaping and self-learning capabilities.",
    longDescription:
      "Created an intelligent snake game that learns to play autonomously using reinforcement learning techniques. Implemented Q-learning and Deep Q-Network (DQN) algorithms from scratch in Rust with sophisticated reward mechanisms, experience replay, and epsilon-greedy exploration. The AI progressively improves gameplay through self-play, achieving superhuman performance after training.",
    technologies: [
      "Rust",
      "Reinforcement Learning",
      "Q-Learning",
      "DQN",
      "Neural Networks",
      "Game AI",
      "Game Development",
    ],
    githubUrl: "https://github.com/timalsinaditya/rust-snake-reinforcement-learning.git",
    featured: false,
    category: "ml",
    startDate: "2024-06",
    status: "completed",
    impact: "Understanding RL fundamentals and reward design",
  },
];

// Helper functions
export const getFeaturedProjects = (): Project[] => {
  return projects.filter((project) => project.featured);
};

export const getProjectsByCategory = (
  category: Project["category"]
): Project[] => {
  return projects.filter((project) => project.category === category);
};

export const getProjectById = (id: string): Project | undefined => {
  return projects.find((project) => project.id === id);
};

export const getProjectStats = () => {
  const total = projects.length;
  const featured = projects.filter((p) => p.featured).length;
  const withPapers = projects.filter((p) => p.paperUrl).length;
  const withMetrics = projects.filter((p) => p.metrics && p.metrics.length > 0).length;

  return {
    total,
    featured,
    withPapers,
    withMetrics,
    byCategory: {
      ml: getProjectsByCategory("ml").length,
      systems: getProjectsByCategory("systems").length,
      networks: getProjectsByCategory("networks").length,
    },
  };
};